//
//  Person.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 24, 2026
//  Author: Bob Keathley
//
//  One person's User Notebook (§6v), generated from their SOUL.md (no questions
//  are asked). Shows the SOUL.md (advisory) and personas (§6w) the Notebook is
//  made from, and the Notebook in effect. The person signs their own; a team admin or above can send a
//  signed version back with a reason. `#/hierarchy/users/me` is the caller's
//  own page (GET /levels/users/me), attached to a level or not.
//
import { useCallback, useEffect, useState } from 'react'
import { api } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import { canSignLevel, levelErrorText, mePath, nodePath, notebookPath, versionsPath, type LevelBirth, type LevelNotebook, type LevelVersion, type PersonMe, type PersonRow, type SendBackResult, type SentBack } from '@shared/api/levels'
import { isPrivateKind, kindLabel, normalizeProfile, profilePath, sendBackPath, type Persona, type ProfileBody } from '@shared/api/people'
import type { Session, TenantRole } from '@shared/auth'
import { href } from '@shared/router'
import { AdvisoryList, SourcesTable, signerName } from './Level'
import { when } from './Projects'

const short = (s: string | null | undefined) => (s ?? '').slice(0, 12)

/** The version whose rules are in effect, and a sent-back one newer than it (the person's banner). */
function sentBackBanner(versions: LevelVersion[]): (SentBack & { version: number }) | null {
  const inEffect = versions.find((v) => v.status === 'in_effect')?.version ?? 0
  const v = versions.find((x) => x.status === 'sent_back' && x.version > inEffect && x.sent_back)
  return v && v.sent_back ? { ...v.sent_back, version: v.version } : null
}

function PersonaList({ personas }: { personas: Persona[] }) {
  if (personas.length === 0) return <p className="hint">No personas submitted.</p>
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {personas.map((p) => (
        <li key={p.id} style={{ margin: '8px 0' }}>
          <b>{p.label}</b> <span className="pill">{kindLabel(p.kind)}</span> <span className="hint">{p.source === 'claude-suggested' ? 'suggested by Claude' : 'written by the person'}</span>
          {isPrivateKind(p.kind)
            ? <div className="hint">Details stay on the person’s Mac.</div>
            : (
              <div className="hint">
                {p.priorities && <div>Priorities: {p.priorities}</div>}
                {p.tone && <div>Tone: {p.tone}</div>}
                {p.limits && <div>Limits: {Array.isArray(p.limits) ? p.limits.join('; ') : p.limits}</div>}
                {p.teams && p.teams.length > 0 && <div>Teams: {p.teams.join(', ')}</div>}
                {p.projects && p.projects.length > 0 && <div>Projects: {p.projects.join(', ')}</div>}
              </div>
            )}
        </li>
      ))}
    </ul>
  )
}

/** SOUL.md and personas as the signer sees them: advisory text, never enforced. */
function ProfilePanel({ profile, error, signedVersion }: { profile: ProfileBody | null; error: string | null; signedVersion: number | null }) {
  const cur = profile?.current ?? null
  return (
    <>
      <h3 style={{ marginTop: 0 }}>Profile <span className="pill off" title="Advisory text only: it is never compiled into rules">advisory</span></h3>
      {error && <p className="err" role="alert">{error}</p>}
      {!error && !cur && <p className="hint">No profile submitted yet. It comes from the person’s Mac, or they can write it there.</p>}
      {cur && (
        <>
          <p className="hint">
            Version {cur.version} · {cur.generated_by}{cur.edited_by_person ? ' · edited by the person' : ''} · {new Date(cur.submitted_at).toLocaleString()}
            {signedVersion !== null && cur.version > signedVersion && <> · <b>newer than the signed Notebook (version {signedVersion}); it is sealed in when they sign again</b></>}
            {signedVersion === null && <> · not yet sealed into a signed Notebook</>}
          </p>
          {cur.soul_md ? <pre className="body">{cur.soul_md}</pre> : <p className="hint">The profile is empty.</p>}
          <h4>Personas</h4>
          <PersonaList personas={cur.personas ?? []} />
        </>
      )}
    </>
  )
}

export default function Person({ session, userId }: { session: Session; userId: string }) {
  const uid = userId === 'me' ? session.user.id : userId
  const node = nodePath('user', uid)
  const token = session.token
  const role = session.user.role as TenantRole
  const isSelf = session.user.id === uid
  const mayBirth = canSignLevel(role, 'user')
  const [person, setPerson] = useState<PersonRow | null | 'none'>(null)
  const [nb, setNb] = useState<LevelNotebook | null>(null)
  const [versions, setVersions] = useState<LevelVersion[] | null>(null)
  const [profile, setProfile] = useState<ProfileBody | null>(null)
  const [profileErr, setProfileErr] = useState<string | null>(null)
  const [sentBack, setSentBack] = useState<(SentBack & { version: number }) | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [busy, setBusy] = useState<'sign' | 'send-back' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [signed, setSigned] = useState<LevelBirth | null>(null)
  const [returned, setReturned] = useState<SendBackResult | null>(null)
  const [reason, setReason] = useState('')

  const load = useCallback(async () => {
    const fail = (e: { code: string; message: string; details?: unknown }) => setLoadErr(e.code === 'not_found' ? 'This person does not exist.' : levelErrorText(e))
    if (userId === 'me') {
      const r = await api<PersonMe>(mePath, {}, token)
      if (!isOk(r)) { fail(r.error); return }
      const m = r.data
      setNb(m.notebook); setVersions(m.versions ?? []); setProfile(normalizeProfile(m.profile, m.notebook.profile_version ?? null)); setProfileErr(null); setSentBack(m.sent_back ?? null); setLoadErr(null)
      setPerson({ user_id: m.user_id, email: m.email, name: m.name, role: m.role, attachment: m.attachment, user_notebook: null })
      return
    }
    const [n, v, p] = await Promise.all([api<LevelNotebook>(notebookPath(node), {}, token), api<{ items: LevelVersion[] }>(versionsPath(node), {}, token), api<ProfileBody>(profilePath(uid), {}, token)])
    if (!isOk(n)) { fail(n.error); return }
    setNb(n.data); setLoadErr(null)
    const items = isOk(v) ? v.data.items : []
    setVersions(items); setSentBack(sentBackBanner(items))
    if (isOk(p)) { setProfile(p.data); setProfileErr(null) } else { setProfile(null); setProfileErr(p.error.code === 'not_found' ? null : levelErrorText(p.error)) }
  }, [userId, uid, node, token])
  useEffect(() => { setNb(null); setVersions(null); setSigned(null); setReturned(null); load() }, [load])
  useEffect(() => {
    if (userId === 'me') return
    let alive = true
    api<{ items: PersonRow[] }>('/levels/people', {}, token).then((r) => { if (alive) setPerson(isOk(r) ? r.data.items.find((p) => p.user_id === uid) ?? 'none' : 'none') })
    return () => { alive = false }
  }, [userId, uid, token])

  const hasSoul = !!profile?.current?.soul_md?.trim()

  async function sign() {
    setBusy('sign'); setError(null)
    const r = await api<LevelBirth>(notebookPath(node), { method: 'PUT', body: JSON.stringify({}) }, token)
    setBusy(null)
    if (!isOk(r)) { setError(levelErrorText(r.error)); return }
    setSigned(r.data); setReturned(null); load()
  }
  async function sendBack() {
    setBusy('send-back'); setError(null)
    const r = await api<SendBackResult>(sendBackPath(uid), { method: 'POST', body: JSON.stringify({ reason: reason.trim() }) }, token)
    setBusy(null)
    if (!isOk(r)) { setError(levelErrorText(r.error)); return }
    setReturned(r.data); setSigned(null); setReason(''); load()
  }

  if (loadErr) return <div className="hierarchy"><p className="err" role="alert">{loadErr}</p><p><a href={href('/hierarchy')}>‹ Hierarchy</a></p></div>
  if (!nb || !versions) return <div className="hierarchy"><p className="hint">Loading…</p></div>
  const who = person && person !== 'none' ? person : null
  const canSign = isSelf || mayBirth
  const inEffect = versions.find((v) => v.status === 'in_effect') ?? (versions.some((v) => v.status) ? null : versions[0] ?? null)
  const canSendBack = mayBirth && !isSelf && !!nb.notebook_id && !!inEffect
  const signedProfileVersion = profile?.signed_profile_version ?? nb.profile_version ?? null

  return (
    <div className="hierarchy">
      <p className="hint"><a href={href('/hierarchy')}>‹ Hierarchy</a></p>
      <h2>{who ? who.name || who.email : 'Person'}{isSelf && <> <span className="pill">you</span></>}</h2>
      <p className="hint">
        {who && <>{who.email} · {who.attachment ? `${who.attachment.level}${who.attachment.ref_name ? `: ${who.attachment.ref_name}` : ''}` : 'not attached to a level'} · </>}
        {nb.notebook_id ? <>User Notebook <span className="mono">{nb.notebook_id}</span> · version {nb.version}{nb.signer && <> · signed {when(nb.signer.at)}</>}</> : 'No User Notebook yet.'}
      </p>
      {sentBack && (
        <p className="err" role="alert">
          <b>Sent back{isSelf ? '' : ' by an admin'}: version {sentBack.version}.</b> {sentBack.reason}{' '}
          <span className="hint">{isSelf ? 'Your previous signed version is back in effect, or none if there was none. Send a new profile if needed and sign again.' : 'Their previous signed version is back in effect until they sign again.'}</span>
        </p>
      )}
      {isSelf && <p className="hint" role="status">This is your own User Notebook. You sign it yourself and it takes effect when you do. A team admin or above can review it and send it back. It can only tighten the levels above it.</p>}
      {error && <p className="err" role="alert">{error}</p>}
      {returned && <p role="status">Sent back version {returned.sent_back_version}. {returned.in_effect_version === null ? 'No signed version is in effect now.' : `Version ${returned.in_effect_version} is back in effect.`} {returned.stale_marked} effective Notebook{returned.stale_marked === 1 ? '' : 's'} marked stale.</p>}
      {signed && <p role="status">Signed version {signed.version}. {signed.stale_marked} effective Notebook{signed.stale_marked === 1 ? '' : 's'} marked stale: re-birth them from each project. Their first effective Notebooks are created in every project they work in.</p>}

      <div className="panel">
        <ProfilePanel profile={profile} error={profileErr} signedVersion={signedProfileVersion} />
        {!hasSoul && !profileErr && <p className="hint">{isSelf ? 'Send a profile from the Signet tray: My Notebook → Draft with Claude. ' : 'They have not sent a profile yet. They send one from the Signet tray (My Notebook → Draft with Claude). '}The Notebook is generated from it.</p>}
        <p>
          {canSign && <button className="btn" disabled={busy !== null || !hasSoul} title={hasSoul ? undefined : 'No profile yet'} onClick={sign}>{busy === 'sign' ? 'Signing…' : `Sign version ${(nb.version ?? 0) + 1}`}</button>}
          {!canSign && <span className="hint">A team admin or above, or the person themselves, signs this Notebook.</span>}
        </p>
      </div>

      {nb.notebook_id && (
        <div className="panel">
          <h3 style={{ marginTop: 0 }}>The Notebook in effect <span className="hint">(version {nb.version})</span></h3>
          <h4>Rules, by where they come from</h4>
          <SourcesTable sources={nb.sources} />
          <AdvisoryList items={nb.advisory} />
        </div>
      )}

      {canSendBack && (
        <div className="panel">
          <h3 style={{ marginTop: 0 }}>Send back</h3>
          <p className="hint">Puts version {inEffect?.version} out of effect. Their previous signed version, if any, comes back in effect until they sign again, and they see your reason on their page.</p>
          <div className="row">
            <input aria-label="Reason for sending back" placeholder="Reason (required)" maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} />
            <button className="btn danger fixed" disabled={busy !== null || !reason.trim()} onClick={sendBack}>{busy === 'send-back' ? 'Sending back…' : 'Send back'}</button>
          </div>
        </div>
      )}

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Versions</h3>
        <table>
          <thead><tr><th>Version</th><th>Signed by</th><th>When</th><th>Status</th><th>Made from</th><th>Hash</th></tr></thead>
          <tbody>
            {versions.length === 0 && <tr><td colSpan={6} className="hint">Not born yet, so there are no versions.</td></tr>}
            {versions.map((v, i) => (
              <tr key={v.birth_cert_id}>
                <td className="mono">{v.version}{i === 0 && v.status !== 'sent_back' && <> <span className="pill ok">current</span></>}</td>
                <td>{signerName(v.signed_by)}{v.self_signed && <> <span className="pill">self-signed</span></>}</td>
                <td className="hint">{when(v.signed_at)}</td>
                <td>
                  {v.status === 'sent_back'
                    ? <><span className="pill off">sent back</span>{v.sent_back && <div className="hint">{v.sent_back.reason}</div>}</>
                    : v.status === 'in_effect' ? <span className="pill ok">in effect</span> : <span className="hint">{v.status === 'superseded' ? 'superseded' : ''}</span>}
                </td>
                <td>{v.source}</td>
                <td className="mono hint" title={v.compiled_hash}>{short(v.compiled_hash)}…</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
